<template>
  <div class="search-bar">
    <a-form :model="formState" name="basic" autocomplete="off" @submit.prevent="onSearch">
      <div class="search-row">
        <a-input-search v-model:value="formState.fileName" placeholder="Search by file name" :loading="loading"
          @search="onSearch" enter-button allowClear />
        <a-tooltip :title="toggleFilter ? 'Hide filters' : 'Show filters'">
          <a-button :type="toggleFilter || activeCount ? 'primary' : 'default'" shape="circle" icon="filter"
            @click="toggleFilter = !toggleFilter" />
        </a-tooltip>
        <a-badge v-if="activeCount" :count="activeCount" class="active-badge" />
      </div>

      <div v-if="toggleFilter" class="filters">
        <section class="filter-group">
          <h4 class="group-title">Conversion</h4>
          <div class="grid">
            <label class="field">
              <span class="field-label">Account</span>
              <a-input v-model:value="formState.account" placeholder="Account name" allowClear @pressEnter="onSearch" />
            </label>
            <label class="field">
              <span class="field-label">Date created</span>
              <a-range-picker v-model:value="formState.dateCreated" @change="onSearch" style="width: 100%" />
            </label>
            <label class="field">
              <span class="field-label">Status</span>
              <a-select v-model:value="formState.status" placeholder="Any status" allowClear @change="onSearch">
                <a-select-option value="success">SUCCESS</a-select-option>
                <a-select-option value="error">ERROR</a-select-option>
                <a-select-option value="in_progress">IN_PROGRESS</a-select-option>
                <a-select-option value="created">CREATED</a-select-option>
              </a-select>
            </label>
            <label class="field">
              <span class="field-label">Company</span>
              <a-input v-model:value="formState.company" placeholder="Company name" allowClear @pressEnter="onSearch" />
            </label>
            <label class="field">
              <span class="field-label">Presentation</span>
              <a-input v-model:value="formState.presentation" placeholder="Presentation name" allowClear @pressEnter="onSearch" />
            </label>
            <label class="field">
              <span class="field-label">Workspace</span>
              <a-input v-model:value="formState.workspace" placeholder="Workspace name" allowClear @pressEnter="onSearch" />
            </label>
            <label class="field">
              <span class="field-label">User</span>
              <a-input v-model:value="formState.user" placeholder="Username" allowClear @pressEnter="onSearch" />
            </label>
          </div>
        </section>

        <section class="filter-group">
          <h4 class="group-title">
            Provenance
            <a-tooltip title="How the model was converted: the wire-format generation, whether the stamps were written by the pipeline or inferred from the bytes, and the workbook's content hash.">
              <a-icon type="info-circle" class="group-help" />
            </a-tooltip>
          </h4>
          <div class="grid">
            <label class="field">
              <span class="field-label">Generation</span>
              <a-select v-model:value="formState.generation" placeholder="Any generation" allowClear @change="onSearch">
                <a-select-option value="v0">v0 · 2-slot metadata</a-select-option>
                <a-select-option value="v1">v1 · stringly literals</a-select-option>
                <a-select-option value="v2">v2 · typed literals</a-select-option>
                <a-select-option value="v3">v3 · faithful types</a-select-option>
                <a-select-option value="v4">v4 · reference range bounds</a-select-option>
              </a-select>
            </label>
            <label class="field">
              <span class="field-label">Stamps</span>
              <a-select v-model:value="formState.stamped" placeholder="Stamped or inferred" allowClear @change="onSearch">
                <a-select-option value="true">Stamped by the pipeline</a-select-option>
                <a-select-option value="false">Inferred from the bytes</a-select-option>
              </a-select>
            </label>
            <label class="field field-wide">
              <span class="field-label">Source workbook hash</span>
              <a-input v-model:value="formState.sourceSha256" placeholder="SHA-256 of the xlsx" allowClear
                class="mono" @pressEnter="onSearch" />
            </label>
          </div>
        </section>

        <div class="filter-actions">
          <a-button @click="resetForm">Clear all</a-button>
          <a-button type="primary" html-type="submit" icon="search">Apply filters</a-button>
        </div>
      </div>
    </a-form>
  </div>
</template>

<script>
export default {
  props: {
    loading: Boolean,
  },
  data() {
    return {
      toggleFilter: false,
      formState: {
        fileName: undefined,
        account: undefined,
        dateCreated: undefined,
        status: undefined,
        company: undefined,
        presentation: undefined,
        workspace: undefined,
        user: undefined,
        generation: undefined,
        stamped: undefined,
        sourceSha256: undefined,
      },
    };
  },
  computed: {
    // Filters in effect besides the file-name search, shown as a badge on the toggle
    activeCount() {
      return Object.entries(this.formState).filter(
        ([key, value]) => key !== "fileName" && value !== undefined && value !== "" && value !== null,
      ).length;
    },
  },
  methods: {
    async onSearch() {
      this.$emit("search", this.formState);
    },
    async resetForm() {
      for (let field in this.formState) this.formState[field] = undefined;
      this.$emit("search", this.formState);
    },
  },
};
</script>

<style lang="scss" scoped>
.search-bar {
  margin-bottom: 12px;
}
.search-row {
  display: flex;
  align-items: center;
  gap: 12px;
  position: relative;
}
.active-badge {
  position: absolute;
  right: -6px;
  top: -6px;
}
.filters {
  margin-top: 12px;
  padding: 16px 20px 12px;
  border: 1px solid #e8e8e8;
  border-radius: 4px;
  background: #fafafa;
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.filter-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.group-title {
  margin: 0;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  gap: 6px;
}
.group-help {
  color: #222377;
  opacity: 0.7;
  cursor: help;
}
.grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 10px 16px;
}
.field {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}
.field-wide {
  grid-column: span 2;
}
.field-label {
  font-size: 12px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.65);
}
.mono ::v-deep input {
  font-family: "SFMono-Regular", Menlo, Consolas, monospace;
  font-size: 12px;
}
.filter-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding-top: 8px;
  border-top: 1px solid #e8e8e8;
}
@media (max-width: 720px) {
  .field-wide { grid-column: span 1; }
}
</style>
